import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Upload, Download, Copy, Settings, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface FormatOptions {
  indentSize: number;
  keywordCase: 'upper' | 'lower' | 'capitalize';
  commaPosition: 'before' | 'after';
  linesBetweenQueries: number;
  maxLineLength: number;
}

export const SqlFormatterTool: React.FC = () => {
  const { t } = useTranslation();
  const [sqlInput, setSqlInput] = useState('');
  const [sqlOutput, setSqlOutput] = useState('');
  const [options, setOptions] = useState<FormatOptions>({
    indentSize: 2,
    keywordCase: 'upper',
    commaPosition: 'after',
    linesBetweenQueries: 2,
    maxLineLength: 80
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sqlKeywords = [
    'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER',
    'ON', 'GROUP', 'BY', 'HAVING', 'ORDER', 'ASC', 'DESC', 'INSERT', 'INTO',
    'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'TABLE', 'ALTER', 'DROP',
    'INDEX', 'VIEW', 'PROCEDURE', 'FUNCTION', 'TRIGGER', 'DATABASE', 'SCHEMA',
    'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'CONSTRAINT', 'UNIQUE', 'NOT',
    'NULL', 'DEFAULT', 'AUTO_INCREMENT', 'IDENTITY', 'CHECK', 'UNION', 'ALL',
    'DISTINCT', 'TOP', 'LIMIT', 'OFFSET', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
    'IF', 'EXISTS', 'IN', 'BETWEEN', 'LIKE', 'IS', 'AND', 'OR', 'AS', 'WITH',
    'RECURSIVE', 'OVER', 'PARTITION', 'ROW_NUMBER', 'RANK', 'DENSE_RANK',
    'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CAST', 'CONVERT', 'SUBSTRING',
    'CONCAT', 'COALESCE', 'ISNULL', 'NULLIF'
  ];

  const formatKeyword = (keyword: string, caseOption: string): string => {
    switch (caseOption) {
      case 'upper':
        return keyword.toUpperCase();
      case 'lower':
        return keyword.toLowerCase();
      case 'capitalize':
        return keyword.charAt(0).toUpperCase() + keyword.slice(1).toLowerCase();
      default:
        return keyword;
    }
  };

  const tokenize = (sql: string): string[] => {
    // Simple tokenizer - splits on whitespace and common SQL delimiters
    const tokens: string[] = [];
    let current = '';
    let inString = false;
    let stringChar = '';
    
    for (let i = 0; i < sql.length; i++) {
      const char = sql[i];
      const nextChar = sql[i + 1];
      
      if (!inString && (char === '"' || char === "'" || char === '`')) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        inString = true;
        stringChar = char;
        current += char;
      } else if (inString && char === stringChar) {
        current += char;
        tokens.push(current);
        current = '';
        inString = false;
        stringChar = '';
      } else if (inString) {
        current += char;
      } else if (/\s/.test(char)) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
      } else if ('(),;'.includes(char)) {
        if (current.trim()) {
          tokens.push(current.trim());
          current = '';
        }
        tokens.push(char);
      } else {
        current += char;
      }
    }
    
    if (current.trim()) {
      tokens.push(current.trim());
    }
    
    return tokens.filter(token => token.length > 0);
  };

  const formatSQL = (sql: string, options: FormatOptions): string => {
    try {
      const tokens = tokenize(sql);
      const indent = ' '.repeat(options.indentSize);
      let formatted = '';
      let indentLevel = 0;
      let lineLength = 0;
      
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        const upperToken = token.toUpperCase();
        const nextToken = tokens[i + 1];
        const prevToken = tokens[i - 1];
        
        // Handle keywords
        if (sqlKeywords.includes(upperToken)) {
          const formattedKeyword = formatKeyword(token, options.keywordCase);
          
          // Major keywords that should start new lines
          if (['SELECT', 'FROM', 'WHERE', 'GROUP', 'HAVING', 'ORDER', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'ALTER', 'DROP'].includes(upperToken)) {
            if (formatted.trim() && !formatted.endsWith('\n')) {
              formatted += '\n';
            }
            formatted += indent.repeat(indentLevel) + formattedKeyword;
            lineLength = indent.length * indentLevel + formattedKeyword.length;
          }
          // JOIN keywords
          else if (['JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER'].includes(upperToken)) {
            if (upperToken === 'JOIN' || (upperToken === 'INNER' && nextToken?.toUpperCase() === 'JOIN')) {
              formatted += '\n' + indent.repeat(indentLevel) + formattedKeyword;
              lineLength = indent.length * indentLevel + formattedKeyword.length;
            } else {
              formatted += ' ' + formattedKeyword;
              lineLength += 1 + formattedKeyword.length;
            }
          }
          // Other keywords
          else {
            formatted += ' ' + formattedKeyword;
            lineLength += 1 + formattedKeyword.length;
          }
        }
        // Handle commas
        else if (token === ',') {
          if (options.commaPosition === 'after') {
            formatted += token;
            if (nextToken && ![')', ';'].includes(nextToken)) {
              formatted += '\n' + indent.repeat(indentLevel + 1);
              lineLength = indent.length * (indentLevel + 1);
            }
          } else {
            formatted += '\n' + indent.repeat(indentLevel + 1) + token + ' ';
            lineLength = indent.length * (indentLevel + 1) + 2;
          }
        }
        // Handle parentheses
        else if (token === '(') {
          formatted += token;
          indentLevel++;
          lineLength += 1;
        }
        else if (token === ')') {
          indentLevel = Math.max(0, indentLevel - 1);
          formatted += token;
          lineLength += 1;
        }
        // Handle semicolon
        else if (token === ';') {
          formatted += token;
          if (i < tokens.length - 1) {
            formatted += '\n'.repeat(options.linesBetweenQueries + 1);
            lineLength = 0;
          }
        }
        // Handle regular tokens
        else {
          // Check if adding this token would exceed max line length
          if (lineLength + token.length + 1 > options.maxLineLength && lineLength > 0) {
            formatted += '\n' + indent.repeat(indentLevel + 1) + token;
            lineLength = indent.length * (indentLevel + 1) + token.length;
          } else {
            if (formatted && !formatted.endsWith(' ') && !formatted.endsWith('\n') && !formatted.endsWith('(')) {
              formatted += ' ';
              lineLength += 1;
            }
            formatted += token;
            lineLength += token.length;
          }
        }
      }
      
      return formatted.trim();
    } catch (error) {
      throw new Error('Failed to format SQL: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleFormat = () => {
    if (!sqlInput.trim()) {
      toast.error(t('common.enterSqlCode'));
      return;
    }

    try {
      const formatted = formatSQL(sqlInput, options);
      setSqlOutput(formatted);
      toast.success(t('common.sqlFormattedSuccessfully'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('common.failedToFormatSql'));
    }
  };

  const handleMinify = () => {
    if (!sqlInput.trim()) {
      toast.error('Please enter SQL code');
      return;
    }

    try {
      // Simple minification - remove extra whitespace
      const minified = sqlInput
        .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
        .replace(/\s*([(),;])\s*/g, '$1')  // Remove spaces around punctuation
        .trim();
      
      setSqlOutput(minified);
      toast.success('SQL minified successfully');
    } catch (error) {
      toast.error('Failed to minify SQL');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.sql') && file.type !== 'text/plain') {
        toast.error('Please select a SQL file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setSqlInput(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadSQL = () => {
    if (!sqlOutput) {
      toast.error('No formatted SQL to download');
      return;
    }

    const blob = new Blob([sqlOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.sql';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const loadSampleSQL = () => {
    const sampleSQL = `SELECT u.id, u.name, u.email, p.title, p.content, c.name as category FROM users u INNER JOIN posts p ON u.id = p.user_id LEFT JOIN categories c ON p.category_id = c.id WHERE u.active = 1 AND p.published_at IS NOT NULL ORDER BY p.created_at DESC LIMIT 10;`;
    setSqlInput(sampleSQL);
  };

  const clearAll = () => {
    setSqlInput('');
    setSqlOutput('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="w-5 h-5" />
            <span>SQL Formatter</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload SQL
            </Button>
            <Button
              onClick={loadSampleSQL}
              variant="outline"
            >
              Load Sample
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              Clear All
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".sql,.txt"
            onChange={handleFileUpload}
            className="hidden"
          />
        </CardContent>
      </Card>

      {/* Format Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Format Options</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Keyword Case
              </label>
              <select
                value={options.keywordCase}
                onChange={(e) => setOptions({ ...options, keywordCase: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="upper">UPPERCASE</option>
                <option value="lower">lowercase</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comma Position
              </label>
              <select
                value={options.commaPosition}
                onChange={(e) => setOptions({ ...options, commaPosition: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="after">After (trailing)</option>
                <option value="before">Before (leading)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Indent Size
              </label>
              <input
                type="number"
                min="1"
                max="8"
                value={options.indentSize}
                onChange={(e) => setOptions({ ...options, indentSize: parseInt(e.target.value) || 2 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Max Line Length
              </label>
              <input
                type="number"
                min="40"
                max="200"
                value={options.maxLineLength}
                onChange={(e) => setOptions({ ...options, maxLineLength: parseInt(e.target.value) || 80 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Lines Between Queries
              </label>
              <input
                type="number"
                min="0"
                max="5"
                value={options.linesBetweenQueries}
                onChange={(e) => setOptions({ ...options, linesBetweenQueries: parseInt(e.target.value) || 2 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SQL Input */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>SQL Input</span>
              <div className="flex space-x-2">
                <Button
                  onClick={handleFormat}
                  disabled={!sqlInput.trim()}
                  variant="primary"
                  size="sm"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Format
                </Button>
                <Button
                  onClick={handleMinify}
                  disabled={!sqlInput.trim()}
                  variant="outline"
                  size="sm"
                >
                  Minify
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={sqlInput}
              onChange={(e) => setSqlInput(e.target.value)}
              placeholder="Paste your SQL code here or upload a file..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* SQL Output */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Formatted SQL</span>
              {sqlOutput && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => copyToClipboard(sqlOutput)}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    onClick={downloadSQL}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={sqlOutput}
              readOnly
              placeholder="Formatted SQL will appear here..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Automatic keyword recognition and formatting</li>
                <li>• Customizable indentation and line length</li>
                <li>• Multiple keyword case options</li>
                <li>• Comma positioning control</li>
                <li>• SQL minification for production</li>
                <li>• File upload and download support</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Supported SQL:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• SELECT, INSERT, UPDATE, DELETE statements</li>
                <li>• JOIN operations and subqueries</li>
                <li>• CREATE, ALTER, DROP statements</li>
                <li>• Functions and expressions</li>
                <li>• Common SQL keywords and syntax</li>
                <li>• Multiple query formatting</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
